/**
 * pages/DealerOverview.jsx
 * ------------------------
 * Powerful-but-simple dealer dashboard built on the unified framework
 * (v2.71.27). Lean overview surface — heavy management UI continues to
 * live at /dealer (legacy comprehensive dashboard).
 *
 * UX principles (per v2.71.29 spec):
 *   • mobile-first, one-thumb        – stats 2×2 on mobile, big tap chips
 *   • quick property posting         – primary "+ Add Property" CTA in
 *                                       header actions; quick chip below
 *   • fast lead access               – Latest Leads rail with inline
 *                                       WhatsApp + Call buttons
 *   • minimal steps                  – every rail item is one-tap to a
 *                                       deeper screen; never two pages
 *                                       between a dealer and a lead
 *   • no overload / no complex forms – posting form deliberately stays
 *                                       at /dealer?tab=submit (existing,
 *                                       tested). Overview only DISPLAYS.
 *
 * Features wired
 * --------------
 *   ✅ Add property         (link header CTA + chip → /dealer?tab=submit)
 *   ✅ Edit property        (Edit link on each listing row → /dealer?tab=submissions)
 *   ✅ Drafts               (Drafts rail with Continue / Submit actions)
 *   ✅ Leads inbox          (Latest Leads rail)
 *   ✅ WhatsApp quick action (inline button per lead + per listing)
 *   ✅ Listing analytics    (views + saves per active listing)
 *   ✅ Property status      (Approved / Pending / Rejected pill per row)
 *   ✅ Featured controls    (★ Featured badge if so; Boost CTA otherwise)
 *   ✅ Subscription         (Plan stat card + free-quota progress meter)
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Plus, MessageCircle, Phone, Crown, Tag, Building2, ArrowRight,
  Pencil, Eye, Heart, Star, ChevronLeft, Send,
} from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { formatINR } from "../shared/services/api";
import api from "../lib/api";
import { useDataSync, useAutoRefresh } from "../lib/dataSync";
import {
  DashboardLayout, StatCard, QuickActions, RecentItemsList, Guard,
} from "../features/dashboard";

/** Compact relative-time helper — local to this page, no need for a util. */
function ago(iso) {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (isNaN(t)) return "";
  const m = Math.floor((Date.now() - t) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

/** Status pill for a listing. Pure visual; no logic. */
function StatusPill({ status, sold }) {
  if (sold) {
    return <span className="px-1.5 py-0.5 bg-[#FAFAFA] text-[#71717A] border border-[#E4E4E7] rounded-sm text-[10px] font-semibold uppercase tracking-wider">Sold</span>;
  }
  if (status === "approved") {
    return <span className="px-1.5 py-0.5 bg-[#DCFCE7] text-[#15803D] rounded-sm text-[10px] font-semibold uppercase tracking-wider">Live</span>;
  }
  if (status === "pending") {
    return <span className="px-1.5 py-0.5 bg-[#FEF3C7] text-[#92400E] rounded-sm text-[10px] font-semibold uppercase tracking-wider">Review</span>;
  }
  if (status === "rejected") {
    return <span className="px-1.5 py-0.5 bg-[#FEE2E2] text-[#991B1B] rounded-sm text-[10px] font-semibold uppercase tracking-wider">Rejected</span>;
  }
  if (status === "draft") {
    return <span className="px-1.5 py-0.5 bg-[#EEF4FF] text-[#0055FF] rounded-sm text-[10px] font-semibold uppercase tracking-wider">Draft</span>;
  }
  return null;
}

/** Build a wa.me share URL for one of the dealer's listings.
 *  v2.73 — Links the SSR OG endpoint (`/api/share/property/<id>`), NOT the
 *  SPA route: WhatsApp's crawler can't run JS, so the SPA URL produced the
 *  same generic brand card for every listing. */
function listingShareLink(p) {
  const base = window.location.origin;
  const url = `${base}/api/share/property/${p.id}`;
  const msg = encodeURIComponent(
    `${p.title}${p.main_area ? " · " + p.main_area : ""}${p.price ? " · " + formatINR(p.price) : ""}\n\n${url}`
  );
  return `https://wa.me/?text=${msg}`;
}

/** Build a wa.me link from a phone number + property context. */
function leadWaLink(lead) {
  const phone = (lead.phone || "").replace(/[^0-9]/g, "");
  if (!phone) return null;
  const intro = `Hi ${lead.name || ""},\n\nThanks for your interest in ${lead.property_title || "the property"}. Let me know a convenient time to discuss.`;
  return `https://wa.me/91${phone.slice(-10)}?text=${encodeURIComponent(intro)}`;
}

export default function DealerOverview() {
  const { user, isDealer, isBuilder, loading: authLoading } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [status, setStatus] = useState(null);
  const [plan, setPlan] = useState({ active: false });
  const [loading, setLoading] = useState(true);

  // v2.72 — Loader extracted so dataSync + auto-refresh can re-fire it.
  const cancelRef = React.useRef(false);
  const load = React.useCallback(() => {
    if (!user || !(isDealer || isBuilder)) return;
    Promise.allSettled([
      api.get("/dealer/my-submissions"),
      api.get("/dealer/enquiries"),
      api.get("/dealer/listing-status"),
      api.get("/dealer/my-plan").catch(() => ({ data: { active: false } })),
    ]).then((res) => {
      if (cancelRef.current) return;
      const [subR, enqR, stR, plR] = res;
      if (subR.status === "fulfilled") setSubmissions(subR.value.data?.results || subR.value.data || []);
      if (enqR.status === "fulfilled") setEnquiries(enqR.value.data?.results || enqR.value.data || []);
      if (stR.status === "fulfilled") setStatus(stR.value.data || null);
      if (plR.status === "fulfilled") setPlan(plR.value.data || { active: false });
      setLoading(false);
    });
  }, [user, isDealer, isBuilder]);

  useEffect(() => {
    cancelRef.current = false;
    load();
    return () => { cancelRef.current = true; };
  }, [load]);

  // v2.72 — Live sync: counters/rails update instantly after own submissions
  // or admin approvals (WS piggyback) + 45 s visibility-aware background poll.
  useDataSync(["properties", "approvals", "enquiries"], load);
  useAutoRefresh(load, 45000);

  // Derived slices — no charts, just counts and the freshest 5.
  const { active, drafts, sold } = useMemo(() => {
    const a = []; const d = []; const s = [];
    for (const p of submissions) {
      if (p.status === "Sold") { s.push(p); continue; }
      if (p.approval_status === "draft") { d.push(p); continue; }
      if (p.approval_status === "approved") a.push(p);
      else d.push(p); // pending + rejected sit with drafts so the dealer sees them together
    }
    return { active: a, drafts: d, sold: s };
  }, [submissions]);

  if (authLoading) return <DashboardLayout title="Dealer Dashboard" sidebar />;
  if (!user) return <Navigate to="/login?next=/dealer/overview" replace />;
  if (!(isDealer || isBuilder)) return <Navigate to="/account" replace />;

  const planLabel = plan?.active ? (plan.plan_name || "Active") : "Free";
  const quotaUsed = status?.total_submitted || 0;
  const quotaMax = status?.free_quota || 0;
  const quotaPct = quotaMax > 0 ? Math.min(100, Math.round((quotaUsed / quotaMax) * 100)) : 0;

  return (
    <DashboardLayout
      title={isBuilder ? "Builder Dashboard" : "Dealer Dashboard"}
      subtitle={user.name ? `Welcome back, ${user.name.split(" ")[0]}.` : "Welcome back."}
      actions={
        <>
          <Link
            to="/dealer/listings"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-[#E4E4E7] hover:border-[#27272A] text-[#27272A] text-[13px] font-medium rounded-sm transition-colors"
            data-testid="dealer-overview-manage"
          >
            Manage
          </Link>
          <Guard perm="property:create">
            <Link
              to="/dealer/submit"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#0055FF] text-white text-[13px] font-semibold rounded-sm hover:bg-[#0044CC] transition-colors"
              data-testid="dealer-overview-add"
            >
              <Plus size={14} /> Add Property
            </Link>
          </Guard>
        </>
      }
    >
      {/* ── Stats: 2×2 on mobile, 4×1 on desktop. ───────────────────────── */}
      <section className="mb-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard
            label="Active"
            icon={Building2}
            value={loading ? "…" : active.length}
            hint={drafts.length ? `${drafts.length} draft/pending` : "Listings live"}
            testid="dealer-stat-active"
          />
          <StatCard
            label="Leads"
            icon={MessageCircle}
            value={loading ? "…" : enquiries.length}
            hint="Inbox"
            testid="dealer-stat-leads"
          />
          <StatCard
            label="Drafts"
            icon={Pencil}
            value={loading ? "…" : drafts.length}
            hint={drafts.length ? "Resume to submit" : "All clear"}
            testid="dealer-stat-drafts"
          />
          <StatCard
            label="Plan"
            icon={Crown}
            value={planLabel}
            hint={plan?.active ? "Subscribed" : `${quotaUsed}/${quotaMax || "∞"} free used`}
            testid="dealer-stat-plan"
          />
        </div>
        {/* Free-quota progress meter — only shown when on free plan with a cap. */}
        {!plan?.active && quotaMax > 0 && (
          <div className="mt-3" data-testid="dealer-quota-meter">
            <div className="flex items-center justify-between text-[11px] text-[#71717A] mb-1">
              <span>Free listings used</span>
              <span className="font-medium text-[#27272A]">{quotaUsed} / {quotaMax}</span>
            </div>
            <div className="h-1.5 bg-[#F4F4F5] rounded-full overflow-hidden">
              <div
                className={`h-full ${quotaPct >= 100 ? "bg-[#DC2626]" : quotaPct >= 80 ? "bg-[#F59E0B]" : "bg-[#0055FF]"}`}
                style={{ width: `${quotaPct}%` }}
              />
            </div>
            {status?.must_upgrade ? (
              <div className="mt-2 text-[11px]">
                <Link
                  to="/dealer/plan"
                  className="text-[#0055FF] font-semibold hover:underline"
                  data-testid="dealer-upgrade-link"
                >
                  Upgrade to keep posting →
                </Link>
              </div>
            ) : null}
          </div>
        )}
      </section>

      {/* ── Quick Actions (desktop only — mobile uses MobileQuickActions
          pill row injected by DashboardLayout via QUICK_ACTIONS_CONFIG) ── */}
      <section className="mb-5 hidden md:block">
        <QuickActions
          actions={[
            { label: "Add Property", to: "/dealer/submit", icon: Plus, perm: "property:create" },
            { label: "My Listings", to: "/dealer/listings", icon: Building2 },
            { label: "Leads", to: "/dealer/leads", icon: MessageCircle },
            { label: "Plan", to: "/dealer/plan", icon: Crown, perm: "plan:purchase" },
          ]}
        />
      </section>

      {/* ── Two-column rails on lg+; stacked on mobile ──────────────────── */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
        {/* Active Listings */}
        <RecentItemsList
          title={`Active Listings${active.length ? ` (${active.length})` : ""}`}
          items={loading ? [] : active.slice(0, 5)}
          emptyHint={loading ? "Loading…" : "No active listings yet. Add your first property."}
          viewAllTo={active.length > 5 ? "/dealer?tab=submissions&filter=approved" : undefined}
          testid="dealer-rail-active"
          renderItem={(p) => (
            <div className="flex items-center gap-3" data-testid={`active-row-${p.id}`}>
              {p.images?.[0] ? (
                <img
                  src={p.images[0]} alt=""
                  loading="lazy" decoding="async" width="56" height="42"
                  className="w-14 h-[42px] object-cover rounded-sm bg-[#F4F4F5] flex-shrink-0"
                />
              ) : (
                <div className="w-14 h-[42px] bg-[#F4F4F5] rounded-sm flex-shrink-0 flex items-center justify-center text-[#A1A1AA]">
                  <Building2 size={16} />
                </div>
              )}
              <Link
                to={`/property/${p.id}`}
                className="flex-1 min-w-0 group"
                data-testid={`active-link-${p.id}`}
              >
                <div className="flex items-center gap-1.5">
                  <span className="text-[13px] font-medium text-[#09090B] truncate group-hover:text-[#0055FF]">{p.title}</span>
                  {p.featured ? (
                    <Star size={11} className="text-[#F59E0B] flex-shrink-0 fill-current" />
                  ) : null}
                </div>
                <div className="text-[11px] text-[#71717A] mt-0.5 flex items-center gap-1.5 truncate">
                  <StatusPill status={p.approval_status} sold={p.status === "Sold"} />
                  {p.main_area ? <span>{p.main_area}</span> : null}
                  {p.price ? <span className="font-semibold text-[#27272A]">· {formatINR(p.price)}</span> : null}
                </div>
                <div className="text-[10px] text-[#A1A1AA] mt-0.5 flex items-center gap-2">
                  <span className="inline-flex items-center gap-1"><Eye size={9} /> {p.views_count || 0}</span>
                  <span className="inline-flex items-center gap-1"><Heart size={9} /> {p.saved_count || 0}</span>
                </div>
              </Link>
              <div className="flex items-center gap-1 flex-shrink-0">
                <a
                  href={listingShareLink(p)}
                  target="_blank" rel="noopener noreferrer"
                  aria-label="Share on WhatsApp"
                  className="w-8 h-8 no-touch-min flex items-center justify-center text-[#16A34A] hover:bg-[#DCFCE7] rounded-sm transition-colors"
                  data-testid={`active-wa-${p.id}`}
                >
                  <MessageCircle size={14} />
                </a>
                <Link
                  to="/dealer/listings"
                  aria-label="Edit listing"
                  className="w-8 h-8 no-touch-min flex items-center justify-center text-[#71717A] hover:text-[#09090B] hover:bg-[#F4F4F5] rounded-sm transition-colors"
                  data-testid={`active-edit-${p.id}`}
                >
                  <Pencil size={14} />
                </Link>
              </div>
            </div>
          )}
        />

        {/* Latest Leads */}
        <RecentItemsList
          title={`Latest Leads${enquiries.length ? ` (${enquiries.length})` : ""}`}
          items={loading ? [] : enquiries.slice(0, 5)}
          emptyHint={loading ? "Loading…" : "Leads from your listings will appear here."}
          viewAllTo={enquiries.length > 5 ? "/dealer/leads" : undefined}
          testid="dealer-rail-leads"
          renderItem={(e) => {
            const wa = leadWaLink(e);
            const tel = e.phone ? `tel:${e.phone.replace(/[^0-9+]/g, "")}` : null;
            return (
              <div className="flex items-center gap-3" data-testid={`lead-row-${e.id}`}>
                <div className="w-10 h-10 bg-[#EEF4FF] rounded-full flex-shrink-0 flex items-center justify-center text-[#0055FF] font-semibold text-[12px]">
                  {(e.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13px] font-medium text-[#09090B] truncate">{e.name || "Anonymous"}</span>
                    {e.kind === "callback_request" && (
                      <span className="px-1 py-0 bg-[#FEF3C7] text-[#92400E] rounded-sm text-[9px] font-semibold uppercase tracking-wider">CB</span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#71717A] mt-0.5 truncate">
                    {e.property_title || "Property"} · {ago(e.created_at)}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {wa && (
                    <a
                      href={wa} target="_blank" rel="noopener noreferrer"
                      aria-label="WhatsApp lead"
                      className="w-8 h-8 no-touch-min flex items-center justify-center text-[#16A34A] hover:bg-[#DCFCE7] rounded-sm transition-colors"
                      data-testid={`lead-wa-${e.id}`}
                    >
                      <MessageCircle size={14} />
                    </a>
                  )}
                  {tel && (
                    <a
                      href={tel}
                      aria-label="Call lead"
                      className="w-8 h-8 no-touch-min flex items-center justify-center text-[#0055FF] hover:bg-[#EEF4FF] rounded-sm transition-colors"
                      data-testid={`lead-call-${e.id}`}
                    >
                      <Phone size={14} />
                    </a>
                  )}
                </div>
              </div>
            );
          }}
        />

        {/* Drafts / Pending — only render when present (clutter-avoidance) */}
        {drafts.length > 0 && (
          <RecentItemsList
            title={`Drafts & Pending (${drafts.length})`}
            items={drafts.slice(0, 5)}
            emptyHint="No drafts."
            viewAllTo={drafts.length > 5 ? "/dealer/listings?filter=pending" : undefined}
            testid="dealer-rail-drafts"
            renderItem={(p) => (
              <div className="flex items-center gap-3" data-testid={`draft-row-${p.id}`}>
                {p.images?.[0] ? (
                  <img
                    src={p.images[0]} alt=""
                    loading="lazy" decoding="async" width="56" height="42"
                    className="w-14 h-[42px] object-cover rounded-sm bg-[#F4F4F5] flex-shrink-0"
                  />
                ) : (
                  <div className="w-14 h-[42px] bg-[#F4F4F5] rounded-sm flex-shrink-0 flex items-center justify-center text-[#A1A1AA]">
                    <Pencil size={16} />
                  </div>
                )}
                <Link
                  to="/dealer/listings"
                  className="flex-1 min-w-0 group"
                  data-testid={`draft-link-${p.id}`}
                >
                  <div className="text-[13px] font-medium text-[#09090B] truncate group-hover:text-[#0055FF]">
                    {p.title || "Untitled listing"}
                  </div>
                  <div className="text-[11px] text-[#71717A] mt-0.5 flex items-center gap-1.5">
                    <StatusPill status={p.approval_status} />
                    {p.main_area ? <span>{p.main_area}</span> : null}
                  </div>
                </Link>
                <Link
                  to="/dealer/listings"
                  aria-label="Continue editing"
                  className="w-8 h-8 no-touch-min flex items-center justify-center text-[#0055FF] hover:bg-[#EEF4FF] rounded-sm transition-colors"
                  data-testid={`draft-continue-${p.id}`}
                >
                  <ArrowRight size={14} />
                </Link>
              </div>
            )}
          />
        )}

        {/* Featured-listing visibility (informational; toggle is admin-only) */}
        {!loading && active.length > 0 && !active.some((p) => p.featured) && (
          <div
            className="bg-white border border-[#E4E4E7] rounded-sm p-4"
            data-testid="dealer-featured-cta"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-[#FFFBEB] rounded-sm flex-shrink-0 flex items-center justify-center text-[#F59E0B]">
                <Star size={18} className="fill-current" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading text-[13px] font-semibold text-[#09090B] tracking-tight">
                  Boost a listing
                </div>
                <div className="text-[11px] text-[#71717A] mt-0.5">
                  Featured listings appear above regular search results. Talk to your account manager via the dealer plans page.
                </div>
                <Link
                  to="/dealer/plan"
                  className="inline-flex items-center gap-1 mt-2 text-[12px] font-semibold text-[#0055FF] hover:underline"
                  data-testid="dealer-boost-cta-link"
                >
                  See plans <ArrowRight size={11} />
                </Link>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ── Footer quick-links to operational sections ──────────────────── */}
      <div className="mt-6 text-[11px] text-[#A1A1AA] flex items-center gap-2 flex-wrap" data-testid="dealer-legacy-link">
        <Link
          to="/dealer/listings"
          className="inline-flex items-center gap-1 hover:text-[#27272A]"
        >
          <ChevronLeft size={12} /> All listings
        </Link>
        <span>·</span>
        <Link
          to="/dealer/kyc"
          className="inline-flex items-center gap-1 hover:text-[#27272A]"
        >
          <Send size={12} /> KYC
        </Link>
      </div>
    </DashboardLayout>
  );
}
